import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Calendar, Plus, Upload } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { TimelineItem } from './TimelineItem';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { hasLabTag, hasRxTag } from '../utils/tagMatchers';
import { P, RequirePermission } from '@/features/permissions';
import { useUploadedDocuments } from '../api/uploaded-documents-api';
import { type EncounterOut, useEncounters } from '../api/encounters-api';
import { type GeneratedDocumentOut, useDocuments } from '../api/documents-api';
import type { TimelineFilterKind, TimelineItem as TimelineItemType, UploadedDocumentOut } from '../types';

// Plan 08: an uploaded doc counts as a Lab/Prescription if its
// `document_type` matches OR any of its tags is a canonical match.
const isLabUploaded = (u: UploadedDocumentOut): boolean => u.document_type === 'lab_result' || hasLabTag(u.tags);

const isRxUploaded = (u: UploadedDocumentOut): boolean => u.document_type === 'prescription' || hasRxTag(u.tags);

interface UnifiedTimelineProps {
  onOpenEncounter?: (encounter: EncounterOut) => void; // NEW — when omitted, encounters navigate
  onOpenGeneratedDocument: (doc: GeneratedDocumentOut) => void;
  onOpenUploadedDocument: (doc: UploadedDocumentOut) => void;
  onUploadClick: () => void;
  patientId: string;
  showUploadButton?: boolean;
}

const FILTER_OPTIONS: { value: TimelineFilterKind; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'encounter', label: 'Encounters' },
  { value: 'generated', label: 'Generated' },
  { value: 'uploaded', label: 'Uploaded' },
  { value: 'lab', label: 'Labs' },
  { value: 'prescription', label: 'Prescriptions' },
];

export function UnifiedTimeline({
  patientId,
  onUploadClick,
  onOpenEncounter,
  onOpenUploadedDocument,
  onOpenGeneratedDocument,
  showUploadButton = true,
}: UnifiedTimelineProps) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<TimelineFilterKind>('all');

  const { data: encountersData } = useEncounters(patientId ? { limit: 100, patient_id: patientId } : undefined);
  const { data: documentsData } = useDocuments(patientId ? { limit: 100, patient_id: patientId } : undefined);
  const { data: uploadedData } = useUploadedDocuments(patientId ? { limit: 100, patient_id: patientId } : undefined);

  const encounters = encountersData?.data ?? [];
  const documents = documentsData?.data ?? [];
  const uploaded = uploadedData?.data ?? [];

  const labCount = useMemo(() => uploaded.filter(isLabUploaded).length, [uploaded]);
  const prescriptionCount = useMemo(() => uploaded.filter(isRxUploaded).length, [uploaded]);

  const timelineItems = useMemo(() => {
    const items: TimelineItemType[] = [];

    if (filter === 'all' || filter === 'encounter') {
      encounters.forEach((e) => items.push({ data: e, kind: 'encounter', date: e.encounter_date }));
    }

    if (filter === 'all' || filter === 'generated') {
      documents.forEach((d) => items.push({ data: d, kind: 'generated', date: d.generated_at }));
    }

    if (filter === 'all' || filter === 'uploaded') {
      uploaded.forEach((u) => items.push({ data: u, kind: 'uploaded', date: u.document_date }));
    }

    if (filter === 'lab') {
      uploaded.filter(isLabUploaded).forEach((u) => items.push({ data: u, kind: 'lab', date: u.document_date }));
    }

    if (filter === 'prescription') {
      uploaded
        .filter(isRxUploaded)
        .forEach((u) => items.push({ data: u, kind: 'prescription', date: u.document_date }));
    }

    items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    return items;
  }, [encounters, documents, uploaded, filter]);

  const handleItemClick = (item: TimelineItemType) => {
    switch (item.kind) {
      case 'encounter':
        if (onOpenEncounter) {
          onOpenEncounter(item.data);
        } else {
          navigate({
            to: '/encounters/$encounterId',
            params: { encounterId: item.data.id },
          });
        }
        break;
      case 'generated':
        onOpenGeneratedDocument(item.data);
        break;
      case 'lab':
      case 'prescription':
      case 'uploaded':
        onOpenUploadedDocument(item.data);
        break;
    }
  };

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1">
          {FILTER_OPTIONS.map((option) => (
            <Button
              key={option.value}
              size="sm"
              className="h-8 text-xs"
              variant={filter === option.value ? 'default' : 'outline'}
              onClick={() => setFilter(option.value)}>
              {option.label}
              {option.value !== 'all' && (
                <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
                  {option.value === 'encounter'
                    ? encounters.length
                    : option.value === 'generated'
                      ? documents.length
                      : option.value === 'lab'
                        ? labCount
                        : option.value === 'prescription'
                          ? prescriptionCount
                          : uploaded.length}
                </Badge>
              )}
            </Button>
          ))}
        </div>

        {showUploadButton && (
          <RequirePermission permission={P.UPDATE_DOCUMENT}>
            <Button size="sm" variant="outline" onClick={onUploadClick}>
              <Upload className="mr-2 h-4 w-4" />
              Upload Document
            </Button>
          </RequirePermission>
        )}
      </div>

      {/* Timeline items */}
      {timelineItems.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Calendar className="h-10 w-10 mb-2" />
            <p>No items to display.</p>
            <RequirePermission permission={P.CREATE_ENCOUNTER}>
              <Button
                size="sm"
                className="mt-3"
                variant="outline"
                onClick={() => navigate({ to: '/encounters/new', search: { patientId } })}>
                <Plus className="mr-2 h-4 w-4" />
                Create First Encounter
              </Button>
            </RequirePermission>
          </CardContent>
        </Card>
      ) : (
        timelineItems.map((item, index) => (
          <TimelineItem
            key={`${item.kind}-${item.data.id}`}
            item={item}
            isLast={index === timelineItems.length - 1}
            onClick={handleItemClick}
          />
        ))
      )}
    </div>
  );
}
